import React from 'react';

import { WidgetType } from '@/api/rest/resources/types/widget';
import { AccordionSection, AccordionWrapper } from '@/components/widgets/o/Accordion/Accordion';
import { WorkstreamOverview } from '@/pages/shared/home/components/WorkstreamOverview';
import { KnowledgeCards } from '@/pages/shared/knowledge/components/KnowledgeCards';
import { useEarningsChartConfiguration } from '@/pages/shared/portfolio/pages/NaturalCapital/components/EarningsChart';
import { useLandUseDistributionChartConfiguration } from '@/pages/shared/portfolio/pages/NaturalCapital/components/LandUseDistributionChart';
// eslint-disable-next-line import/no-cycle
import { ScenarioBiophysicalDetails } from '@/pages/shared/portfolio/pages/scenarios/components/ScenarioBiophysicalDetails';
import { ScenarioFinancialDetails } from '@/pages/shared/portfolio/pages/scenarios/components/ScenarioFinancialDetails';
import { ScenarioMethodologyDetails } from '@/pages/shared/portfolio/pages/scenarios/components/ScenarioMethodologyDetails';
import { PlotsMap } from '@/pages/shared/projects/components/projectsMap/ProjectsMap';
import { FormRadioGroup } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/components/Controls';
import { HabitatIntactnessThreatsTableCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/HabitatIntactness/components/ThreatsTable/TableCard';
import { AboveGroundCarbon } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/aboveGroundCarbon/AboveGroundCarbonCard';
import { Biodiversity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/biodiversity/Biodiversity';
import { CanopyCover } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/canopyCover/CanopyCover';
import { CanopyHeight } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/canopyHeight/CanopyHeight';
import { BelowGroundCarbon } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/carbon/BelowGroundCarbonCard';
import { ClimateRisk } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/climateRisk/ClimateRisk';
import { CoastalEcosystemCondition } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/coastalEcosystemCondition/CoastalEcosystemCondition';
import { Deforestation } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/deforestation/DeforestationCard';
import { useDegradationDriversDistribution } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/deforestation/DegradationDrivers';
import { EcosystemClassification } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ecosystemClassification/EcosystemClassificationCard';
import { EcosystemIntegrityIndex } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/eii/EiiCard';
import { FloraFaunaComposition } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/floraAndFauna/FloraFaunaComposition';
import { FloraFaunaDiversity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/floraAndFauna/FloraFaunaDiversity';
import { InvasiveSpeciesControl } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/floraAndFauna/InvasiveSpeciesControl';
import { ForestIntelligence } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/forestIntelligence/ForestIntelligence';
import { HabitatConnectivity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/HabitatConnectivity/HabitatConnectivityCard';
import { HabitatIntactness } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatIntactness/HabitatIntactnessCard';
import { HabitatIntactnessThreatsBreakdown } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatThreatsBreakdown/HabitatIntactnessThreatsBreakdownCard';
import { HabitatIntactnessThreatsTable } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatThreatsTable/HabitatIntactnessThreatsTableCard';
import { NaturePositiveIndex } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/indexes/NaturePositiveIndex';
import { ReefFunctionalIndex } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/indexes/ReefFunctionalIndex';
import { IndicatorSpecies } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/indicatorSpecies/IndicatorSpecies';
import { LandCoverChange } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/landCover/LandCover';
import { NDVI } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ndvi/NDVICard';
import { PlotMapWidget } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotMap';
import { PlotOverview } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotOverview';
import { ProtectedAreas } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/protectedAreas/ProtectedAreas';
import { ThreatenedSpecies } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/threatenedSpecies/ThreatenedSpeciesCard';
import { Precipitation } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/water/Precipitation';
import { WaterHoldingCapacity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/water/WaterCard';
import { ReportsList } from '@/pages/shared/reports/components/ReportsList';

import { useRiskAggregatedOverviewConfiguration } from '../ClimateRisk/RiskAggregatedOverview';
import { RiskChart } from '../ClimateRisk/RiskChart';
import { RiskInfo } from '../ClimateRisk/RiskInfo';
import { RiskOverview } from '../ClimateRisk/RiskOverview';
import { RiskSectorChart } from '../ClimateRisk/RiskSectorChart';
import { DetailsCard } from '../DetailsCard/DetailsCard';
import { IndicatorCard } from '../IndicatorCard/IndicatorCard';
import { useAllIndicatorsHealthConfiguration } from '../NatureHealth/IndicatorHealth';
import { ButtonWidget } from './a/Button/Button';
import { IconButtonWidget } from './a/Button/IconButton';
import { Calendar } from './a/Calendar/Calendar';
import { ChartHeading } from './a/ChartHeading/ChartHeading';
import { Divider } from './a/Divider/Divider';
import { FactCapsule } from './a/Fact/FactCapsule';
import { Icon } from './a/Icon/Icon';
import { Image } from './a/Image/Image';
import { MailLink } from './a/Link/Mail';
import { NewTabLink } from './a/Link/NewTabLink';
import { OlListWidget, UlListWidget } from './a/List/List';
import { PendingApprovalFrame } from './a/Misc/PendingApprovalFrame';
import { PendingWidget } from './a/Misc/PendingWidget';
import { PillWidget } from './a/Pill/Pill';
import { ProgressWidget } from './a/Progress/Progress';
import { ProgressCircle } from './a/Progress/ProgressCircle';
import { ProgressBar } from './a/ProgressBar/ProgressBar';
import { StackWidget } from './a/Stack/Stack';
import { TextContainer } from './a/Text/Text';
import { useAdditionalPlotsFilterPillWidget } from './config/AdditionalPlotsFilterPillWidget';
import {
  useEcologicalIntegrityIndex,
  useFormSectionPlotLabelValues,
  usePlotGroupFiltersConfiguration,
  usePlotGroupFiltersManagementPanel,
  usePlotGroupSortConfiguration,
  useStewardedValue,
  useTotalAssets,
  useTotalSize,
} from './config/index';
import { PlotGroupForestIntelligenceChart } from './config/PlotGroupForestIntelligenceChart';
import { useEIIDecompositionChart } from './config/useEIIDecompositionChart';
import { useFactWidget } from './config/useFactWidget';
import { useForestIntelligenceChart } from './config/useForestIntelligenceChart';
import { useFormSectionPlots } from './config/useFormSectionPlots';
import {
  useTotalStocksHeld,
  useTotalStocksHeldValue,
  useTotalStocksIssued,
  useTotalStocksIssuedValue,
  useTotalStocksSold,
  useTotalStocksSoldValue,
} from './config/useStockCards';
import { useSummaryFromRecommendation } from './config/useSummaryFromRecommendation';
import { useTotalAccountValue } from './config/useTotalAccountValue';
import { ConfigurationFromHookRenderer } from './hooks/ConfigurationFromHookRenderer';
import { Currency } from './m/Currency/Currency';
import { SearchBar } from './m/Search/SearchBar';
import { Tabs } from './m/Tabs/Tabs';
import { TooltipContentWidget, TooltipTriggerWidget, TooltipWidget } from './m/Tooltip/Tooltip';
import { TrendWidget } from './m/Trend/Trend';
import { ArticleCard } from './o/ArticleCard/ArticleCard';
import { Card } from './o/Card/Card';
import { CategoriesSummary } from './o/CategoriesSummary/CategoriesSummary';
import { ComplianceFrameworks } from './o/ComplianceFrameworks/ComplianceFrameworks';
import { CompositeGraph } from './o/CompositeGraph/CompositeGraph';
import { DataProcessor } from './o/CompositeGraph/DataProcessor';
import {
  DialogBase,
  DialogBodyWidget,
  DialogCloseWidget,
  DialogContentWidget,
  DialogFooterWidget,
  DialogHeaderWidget,
  DialogWidget,
} from './o/Dialog/Dialog';
import { Explainer } from './o/Explainer/Explainer';
import { ClearFormButton, FormButtonItem, SubmitButton } from './o/Form/Button';
import { FormCalendar } from './o/Form/Calendar';
import { CheckboxWidget } from './o/Form/CheckBox';
import { Form } from './o/Form/Form';
import { FormRadioItem } from './o/Form/FormRadio';
import { FormSelect } from './o/Form/FormSelect';
import { FormSlider } from './o/Form/FormSlider';
import { FormTextInput } from './o/Form/FormTextInput';
import { Picker, PickerContent, PickerTrigger } from './o/Form/Picker';
import { FormSection } from './o/Form/Section';
import { GradientScale } from './o/Gradient/Gradient';
import { LinkCard } from './o/LinkCard/LinkCard';
import { OpacitySlider } from './o/Map/LayerEditMenu/OpacitySlider';
import { MediaMapFormItems } from './o/Map/MediaMap/MediaMapFormItems';
import { NCCard, NCCard2SectionLayout, NCCardHeader, NCCardSectionHeader } from './o/NCCard/NCCard';
import { useOrgCardWidget } from './o/OrgCard/OrgCard';
import { usePlotCardWidget } from './o/PlotCard/PlotCard';
import { RecommendationCardWidget } from './o/RecommendationCard/RecommendationCard';
import { ReportCard } from './o/ReportCard/ReportCard';
import { SimpleTable } from './o/SimpleTable/SimpleTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const WIDGET_TYPE_COMPONENT_MAP: Partial<Record<WidgetType, React.ComponentType<any>>> = {
  [WidgetType.o_card]: Card,
  [WidgetType.o_project_indicator_card]: IndicatorCard,
  [WidgetType.o_details_graph_card]: DetailsCard,
  [WidgetType.o_simple_table]: SimpleTable,
  [WidgetType.o_composite_graph]: CompositeGraph,
  [WidgetType.o_data_processor]: DataProcessor,
  [WidgetType.o_form]: Form,

  [WidgetType.o_form_text_input]: FormTextInput,
  [WidgetType.o_form_slider]: FormSlider,
  [WidgetType.o_form_select]: FormSelect,
  [WidgetType.o_c_physical_risk_tab]: ScenarioBiophysicalDetails,
  [WidgetType.o_c_financial_impact_tab]: ScenarioFinancialDetails,
  [WidgetType.o_c_methodology_tab]: ScenarioMethodologyDetails,
  [WidgetType.o_form_radio_group]: FormRadioGroup,
  [WidgetType.o_form_radio_item]: FormRadioItem,
  [WidgetType.o_form_picker]: Picker,
  [WidgetType.o_form_picker_trigger]: PickerTrigger,
  [WidgetType.o_form_picker_content]: PickerContent,
  [WidgetType.o_form_section]: FormSection,
  [WidgetType.o_form_button]: FormButtonItem,
  [WidgetType.o_form_submit_button]: SubmitButton,
  [WidgetType.o_form_clear_button]: ClearFormButton,
  [WidgetType.o_form_checkbox]: CheckboxWidget,
  [WidgetType.o_form_calendar]: FormCalendar,
  [WidgetType.o_recommendation_default_card]: RecommendationCardWidget,
  [WidgetType.o_c_form_label_values_section]: ConfigurationFromHookRenderer(useFormSectionPlotLabelValues),
  [WidgetType.o_c_form_plots_section]: ConfigurationFromHookRenderer(useFormSectionPlots),

  [WidgetType.o_recommendation_summary]: ConfigurationFromHookRenderer((props) => useSummaryFromRecommendation(props)),
  [WidgetType.o_stewarded_value]: ConfigurationFromHookRenderer(useStewardedValue),
  [WidgetType.o_total_account_value]: ConfigurationFromHookRenderer(useTotalAccountValue),
  [WidgetType.o_total_assets]: ConfigurationFromHookRenderer(useTotalAssets),
  [WidgetType.o_total_size]: ConfigurationFromHookRenderer(useTotalSize),
  [WidgetType.o_ecological_integrity_index]: ConfigurationFromHookRenderer(useEcologicalIntegrityIndex),
  [WidgetType.o_fact_summary]: ConfigurationFromHookRenderer((props) => useFactWidget(props)),
  [WidgetType.o_total_stocks_issued]: ConfigurationFromHookRenderer(useTotalStocksIssued),
  [WidgetType.o_total_stocks_held]: ConfigurationFromHookRenderer(useTotalStocksHeld),
  [WidgetType.o_total_stocks_sold]: ConfigurationFromHookRenderer(useTotalStocksSold),
  [WidgetType.o_total_stocks_issued_value]: ConfigurationFromHookRenderer(useTotalStocksIssuedValue),
  [WidgetType.o_total_stocks_held_value]: ConfigurationFromHookRenderer(useTotalStocksHeldValue),
  [WidgetType.o_total_stocks_sold_value]: ConfigurationFromHookRenderer(useTotalStocksSoldValue),

  [WidgetType.o_dialog_base]: DialogBase,
  [WidgetType.o_dialog]: DialogWidget,
  [WidgetType.o_dialog_header]: DialogHeaderWidget,
  [WidgetType.o_dialog_content]: DialogContentWidget,
  [WidgetType.o_dialog_body]: DialogBodyWidget,
  [WidgetType.o_dialog_close]: DialogCloseWidget,
  [WidgetType.o_dialog_footer]: DialogFooterWidget,

  [WidgetType.o_reports_list]: ReportsList,
  [WidgetType.o_report_card]: ReportCard,
  [WidgetType.o_compliance_frameworks]: ComplianceFrameworks,

  [WidgetType.o_nc_card]: NCCard,
  [WidgetType.o_nc_card_header]: NCCardHeader,
  [WidgetType.o_nc_card_2_section_layout]: NCCard2SectionLayout,
  [WidgetType.o_nc_card_section_header]: NCCardSectionHeader,

  [WidgetType.o_article_card]: ArticleCard,
  [WidgetType.o_link_card]: LinkCard,

  [WidgetType.o_accordion_wrapper]: AccordionWrapper,
  [WidgetType.o_accordion_section]: AccordionSection,

  [WidgetType.o_explainer]: Explainer,
  [WidgetType.o_gradient_scale_graph]: GradientScale,

  [WidgetType.o_media_map_form_items]: MediaMapFormItems,
  [WidgetType.o_map_layer_opacity]: OpacitySlider,

  [WidgetType.o_c_knowledge_cards]: KnowledgeCards,

  [WidgetType.o_c_workstream_overview]: WorkstreamOverview,

  [WidgetType.o_c_plot_group_filters]: ConfigurationFromHookRenderer(usePlotGroupFiltersConfiguration),
  [WidgetType.o_c_plot_group_filters_management]: ConfigurationFromHookRenderer(usePlotGroupFiltersManagementPanel),
  [WidgetType.o_c_plot_group_sort]: ConfigurationFromHookRenderer(usePlotGroupSortConfiguration),
  [WidgetType.o_c_plot_group_additional_plots_filter_pills]: ConfigurationFromHookRenderer((props) =>
    useAdditionalPlotsFilterPillWidget(props),
  ),
  [WidgetType.o_c_indicator_health_overview]: ConfigurationFromHookRenderer(useAllIndicatorsHealthConfiguration),

  [WidgetType.o_c_risk_aggregated_overview]: ConfigurationFromHookRenderer(useRiskAggregatedOverviewConfiguration),
  [WidgetType.o_c_risk_sector_chart]: RiskSectorChart,
  [WidgetType.o_c_risk_overview]: RiskOverview,
  [WidgetType.o_c_risk_chart]: RiskChart,
  [WidgetType.o_c_risk_info]: RiskInfo,
  [WidgetType.o_c_earnings_chart]: ConfigurationFromHookRenderer(useEarningsChartConfiguration),
  [WidgetType.o_c_land_distribution_chart]: ConfigurationFromHookRenderer(useLandUseDistributionChartConfiguration),

  [WidgetType.o_c_ecological_integrity_decomposition]: ConfigurationFromHookRenderer(useEIIDecompositionChart),
  [WidgetType.o_c_forest_intelligence_chart]: ConfigurationFromHookRenderer(useForestIntelligenceChart),
  [WidgetType.o_c_plot_group_forest_intelligence_chart]: PlotGroupForestIntelligenceChart,
  [WidgetType.o_c_deforestation_degradation_drivers_decomposition]: ConfigurationFromHookRenderer(
    useDegradationDriversDistribution,
  ),
  [WidgetType.o_c_plots_map]: PlotsMap,

  [WidgetType.o_c_plot_map]: PlotMapWidget,
  [WidgetType.o_c_plot_overview]: PlotOverview,
  [WidgetType.o_c_plot_indicator_climate_risk]: ClimateRisk,
  [WidgetType.o_c_plot_indicator_protected_areas]: ProtectedAreas,
  [WidgetType.o_c_plot_indicator_threatened_species]: ThreatenedSpecies,
  [WidgetType.o_c_plot_indicator_land_cover_change]: LandCoverChange,
  [WidgetType.o_c_plot_indicator_above_ground_carbon]: AboveGroundCarbon,
  [WidgetType.o_c_plot_indicator_below_ground_carbon]: BelowGroundCarbon,
  [WidgetType.o_c_plot_indicator_water_holding_capacity]: WaterHoldingCapacity,
  [WidgetType.o_c_plot_indicator_precipitation]: Precipitation,
  [WidgetType.o_c_plot_indicator_canopy_height]: CanopyHeight,
  [WidgetType.o_c_plot_indicator_canopy_cover]: CanopyCover,
  [WidgetType.o_c_plot_indicator_biodiversity]: Biodiversity,
  [WidgetType.o_c_plot_indicator_ecosystem_integrity_index]: EcosystemIntegrityIndex,
  [WidgetType.o_c_plot_indicator_ndvi]: NDVI,
  [WidgetType.o_c_plot_indicator_ecosystem_classification]: EcosystemClassification,
  [WidgetType.o_c_plot_indicator_habitat_intactness]: HabitatIntactness,
  [WidgetType.o_c_plot_indicator_habitat_intactness_threats_breakdown]: HabitatIntactnessThreatsBreakdown,
  [WidgetType.o_c_plot_indicator_habitat_intactness_threats_table]: HabitatIntactnessThreatsTable,
  [WidgetType.o_c_plot_indicator_habitat_connectivity]: HabitatConnectivity,
  [WidgetType.o_c_plot_indicator_deforestation]: Deforestation,
  [WidgetType.o_c_plot_indicator_forest_intelligence]: ForestIntelligence,
  [WidgetType.o_c_plot_indicator_reef_functional_index]: ReefFunctionalIndex,
  [WidgetType.o_c_plot_indicator_nature_positive_index]: NaturePositiveIndex,
  [WidgetType.o_c_plot_indicator_indicator_species]: IndicatorSpecies,
  [WidgetType.o_c_plot_indicator_flora_fauna_composition]: FloraFaunaComposition,
  [WidgetType.o_c_plot_indicator_flora_fauna_diversity]: FloraFaunaDiversity,
  [WidgetType.o_c_plot_indicator_invasive_species_control]: InvasiveSpeciesControl,
  [WidgetType.o_c_plot_indicator_coastal_ecosystem_condition]: CoastalEcosystemCondition,
  [WidgetType.o_c_habitat_intactness_threats_table]: HabitatIntactnessThreatsTableCard,

  [WidgetType.m_tooltip]: TooltipWidget,
  [WidgetType.m_tooltip_content]: TooltipContentWidget,
  [WidgetType.m_tooltip_trigger]: TooltipTriggerWidget,
  [WidgetType.m_trend]: TrendWidget,
  [WidgetType.m_currency]: Currency,
  [WidgetType.m_icon_button]: IconButtonWidget,
  [WidgetType.m_search_bar]: SearchBar,
  [WidgetType.m_tabs]: Tabs,

  [WidgetType.a_fact_capsule]: FactCapsule,
  [WidgetType.a_chart_heading]: ChartHeading,
  [WidgetType.a_progress_bar]: ProgressBar,
  [WidgetType.a_icon]: Icon,
  [WidgetType.a_text]: TextContainer,
  [WidgetType.a_pill]: PillWidget,
  [WidgetType.a_button]: ButtonWidget,
  [WidgetType.a_stack]: StackWidget,
  [WidgetType.a_progress]: ProgressWidget,
  [WidgetType.a_ul_list]: UlListWidget,
  [WidgetType.a_ol_list]: OlListWidget,
  [WidgetType.a_calendar]: Calendar,
  [WidgetType.a_image]: Image,
  [WidgetType.a_mail_link]: MailLink,
  [WidgetType.a_new_tab_link]: NewTabLink,
  [WidgetType.a_progress_circle]: ProgressCircle,
  [WidgetType.a_divider]: Divider,
  [WidgetType.o_plot_card]: ConfigurationFromHookRenderer(usePlotCardWidget),
  [WidgetType.o_org_card]: ConfigurationFromHookRenderer(useOrgCardWidget),
  [WidgetType.o_categories_summary]: CategoriesSummary,
  [WidgetType.a_pending_approval_frame]: PendingApprovalFrame,
  [WidgetType.a_pending_widget]: PendingWidget,
};
